import Lang from '@/modules/core/app/services/Lang';
import { VaultPlanType } from '@/modules/estate/constants/VaultPlanType';
import NumeralService from '@/modules/core/app/services/NumeralService';
import { ColumnFormat } from '@/modules/core/app/constants/data.constants';
import { StandardPlanPrices } from '@/modules/estate/constants/StandardPlanPrices';
import { ProductKey } from '@/modules/estate/enums/ProductKey';
import HubSpotProductService from '@/modules/hubspot/services/HubSpotProductService';
import { HubSpotProduct } from '@/modules/hubspot/models/HubSpotProduct';
import StripeCouponService from '@/modules/billing/services/stripe/StripeCouponService';
import { Company } from '@/modules/company/models/Company';
import { getters } from '@/modules/core/app/helpers/store';
import BillingService from '/@/modules/billing/services/BillingService';
import { BillingPlanPrice } from '@/modules/billing/models/BillingPlanPrice';
import { BillingPlanId } from '@/modules/billing/constants/BillingPlanId';
import { BillingPlanIdMapper } from '@/modules/billing/mappers/BillingPlanIdMapper';
import { PartnershipInvitationType } from '@/modules/partnershipinvitation/constants/PartnershipInvitationType';
import FeatureFlagService from '@/modules/featureflag/services/FeatureFlagService';
import { PermissionKey } from '@/modules/auth/constants/PermissionKey';
import { EstateType } from '@/modules/estate/enums/EstateType';

export class SelfServeBillingPlanForm {
  isAlreadyOnPlan: boolean;
  name?: string;
  description?: string;
  subTitle?: string;
  upsellTitle?: string;
  upsellDescription?: string;
  price?: number;
  yearlyPrice?: number;
  isSaasBilling?: boolean;
  billingPlanId?: string;
  inclusions: string[];
  exclusions: string[];
  partnerCompany: Company | undefined;
  isClient: boolean;
  type?: string;
  needs!: string[];
  includedUpsells: string[];
  excludedUpsells: string[];
  isRecommended: boolean;

  productKey?: ProductKey;

  recurringProductKey?: ProductKey;

  chargeRecurringFee?: boolean;

  constructor(model: Partial<SelfServeBillingPlanForm> = {}) {
    this.isAlreadyOnPlan = false;
    this.name = model.name;
    this.description = model.description;
    this.price = model.price;
    this.yearlyPrice = model.yearlyPrice;
    this.isSaasBilling = model.isSaasBilling ?? false;
    this.billingPlanId = model.billingPlanId;
    this.inclusions = model.inclusions ?? [];
    this.exclusions = model.exclusions ?? [];
    this.isClient = model.isClient ?? false;
    this.isRecommended = model.isRecommended ?? false;
    this.type = model.type;
    this.partnerCompany = model.partnerCompany;
    this.chargeRecurringFee = model.chargeRecurringFee;
    this.needs = model.needs ?? [];
    this.includedUpsells = model.includedUpsells ?? [];
    this.excludedUpsells = model.excludedUpsells ?? [];
    this.productKey = model.productKey;
  }

  get computedName(): string {
    if (
      this.isSaasBilling &&
      (this.productKey === ProductKey.INDIVIDUAL_STANDARD_PLAN ||
        this.billingPlanId === BillingPlanId.INDIVIDUAL_STANDARD_PLAN)
    ) {
      return Lang.t('standardplan.online-will-digital-vault');
    }

    return this.name!;
  }

  get isSelfServe(): boolean {
    if (!this.type) {
      return false;
    }

    return VaultPlanType.isSelfServe(this.type);
  }

  get showPrices(): boolean {
    return !this.isSaasBilling;
  }

  setIsRecommended(value: boolean): this {
    this.isRecommended = value;

    return this;
  }

  setProductKey(value: ProductKey): this {
    this.productKey = value;

    return this;
  }

  setRecurringProductKey(value: ProductKey): this {
    this.recurringProductKey = value;

    return this;
  }

  setAsCouples(): this {
    switch (this.productKey) {
      case ProductKey.INDIVIDUAL_BASIC_PLAN:
        this.productKey = ProductKey.COUPLES_BASIC_PLAN;
        this.recurringProductKey = ProductKey.COUPLES_BASIC_PLAN_RECURRING;
        break;
      case ProductKey.INDIVIDUAL_COMPLETE_PLAN:
        this.productKey = ProductKey.COUPLES_COMPLETE_PLAN;
        this.recurringProductKey = ProductKey.COUPLES_COMPLETE_PLAN_RECURRING;
        break;
      case ProductKey.INDIVIDUAL_PROFESSIONAL_EXECUTOR_PLAN:
        this.productKey = ProductKey.COUPLES_PROFESSIONAL_EXECUTOR_PLAN;
        this.recurringProductKey = ProductKey.COUPLES_PROFESSIONAL_EXECUTOR_PLAN_RECURRING;
        break;
    }

    return this;
  }

  setAsIndividual(): this {
    switch (this.productKey) {
      case ProductKey.COUPLES_BASIC_PLAN:
        this.productKey = ProductKey.INDIVIDUAL_BASIC_PLAN;
        this.recurringProductKey = ProductKey.INDIVIDUAL_BASIC_PLAN_RECURRING;
        break;
      case ProductKey.COUPLES_COMPLETE_PLAN:
        this.productKey = ProductKey.INDIVIDUAL_COMPLETE_PLAN;
        this.recurringProductKey = ProductKey.INDIVIDUAL_COMPLETE_PLAN_RECURRING;
        break;
      case ProductKey.COUPLES_PROFESSIONAL_EXECUTOR_PLAN:
        this.productKey = ProductKey.INDIVIDUAL_PROFESSIONAL_EXECUTOR_PLAN;
        this.recurringProductKey = ProductKey.INDIVIDUAL_PROFESSIONAL_EXECUTOR_PLAN_RECURRING;
        break;
    }

    return this;
  }

  setPrices(price?: number, yearlyPrice?: number): this {
    this.price = price;
    this.yearlyPrice = yearlyPrice;

    return this;
  }

  async loadPrices(jurisdictionId?: number): Promise<void> {
    const [products, coupon] = await Promise.all([
      HubSpotProductService.getProductsWithKeys(this.productKeys, jurisdictionId),
      StripeCouponService.getPartnershipInvitationCoupon(jurisdictionId)
    ]);

    this.price = products
      .find((product: HubSpotProduct) => product.propertyProductkey === this.productKey)
      ?.getDiscountedPrice(coupon);
    this.yearlyPrice = products
      .find((product: HubSpotProduct) => product.propertyProductkey === this.recurringProductKey)
      ?.getDiscountedPrice(coupon);
  }

  async loadPlanPrices(
    jurisdictionId?: number,
    companyId?: number,
    partnershipInvitationType?: PartnershipInvitationType
  ): Promise<void> {
    const billingPlanId: BillingPlanId = BillingPlanIdMapper.getBillingPlanId(this.productKey);
    const [billingPlanPrice]: [BillingPlanPrice] = await Promise.all([
      BillingService.calculateBillingPlanPrices({
        billingPlanId,
        jurisdictionId,
        companyId,
        partnershipInvitationType
      }),
      this.loadChargeRecurringFee(jurisdictionId, companyId)
    ]);

    this.price = billingPlanPrice?.price;
    //  todo @olivier refactor rename yearly to recurring
    this.yearlyPrice = billingPlanPrice?.recurringPrice;
    this.isSaasBilling = billingPlanPrice?.isSaasBilling;
  }

  async loadChargeRecurringFee(jurisdictionId?: number, companyId?: number): Promise<void> {
    if (!this.isStandardPlan) {
      return;
    }

    this.chargeRecurringFee = await FeatureFlagService.getEvaluation(
      PermissionKey.CAN_PAY_STANDARD_PLAN_RECURRING_FEE,
      { jurisdictionId, companyId, estateType: EstateType.CLEAR_VAULT.key }
    );
  }

  setAsStandardPlan(
    isRecommended: boolean = false,
    isROC: boolean = true,
    partnerCompany?: Company
  ): this {
    this.isAlreadyOnPlan = false;
    this.name = Lang.t('standardplan.online-will');
    this.description = isROC
      ? Lang.t('standardplan.create-will-ideal')
      : Lang.t('standardplan.create-will-presence');
    this.productKey = ProductKey.INDIVIDUAL_STANDARD_PLAN;
    this.needs = this.getStandardPlanNeeds;
    this.recurringProductKey = ProductKey.INDIVIDUAL_STANDARD_PLAN_RECURRING;
    this.inclusions = SelfServeBillingPlanForm.getStandardPlanInclusions(isROC);
    this.exclusions = SelfServeBillingPlanForm.getStandardPlanExclusions(isROC);
    this.isRecommended = isRecommended;
    this.type = VaultPlanType.STANDARD.key;
    this.partnerCompany = partnerCompany;
    this.chargeRecurringFee = getters.auth.getPermission()?.canPayStandardPlanRecurringFee;

    return this;
  }

  setAsBasicPlan(
    isRecommended: boolean = false,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    isROC: boolean = true,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    partnerCompany?: Company
  ): this {
    this.isAlreadyOnPlan = false;
    this.name = Lang.t('standardplan.basic');
    this.description = isROC
      ? Lang.t('standardplan.create-will-poa-basic')
      : Lang.t('standardplan.create-will-witnesses-basic');
    this.subTitle = Lang.t('standardplan.estate-plan');
    this.upsellTitle = Lang.t('basicplan.in-this-plan');
    this.upsellDescription = Lang.t('basicplan.create-your-will-and-poa');
    this.productKey = ProductKey.INDIVIDUAL_BASIC_PLAN;
    this.recurringProductKey = ProductKey.INDIVIDUAL_BASIC_PLAN_RECURRING;
    this.inclusions = SelfServeBillingPlanForm.getBasicPlanInclusions(isROC);
    this.exclusions = SelfServeBillingPlanForm.getBasicPlanExclusions(isROC);
    this.needs = this.getBasicPlanNeeds;
    this.includedUpsells = this.getIncludedBasicPlanUpsells;
    this.excludedUpsells = this.getExcludedBasicPlanUpsells;
    this.isRecommended = isRecommended;
    this.type = VaultPlanType.BASIC.key;
    this.chargeRecurringFee = true;

    return this;
  }

  setAsCompletePlan(
    isClient: boolean = false,
    isRecommended: boolean = false,
    discountPrice: number | undefined = undefined,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    isROC: boolean = true
  ): this {
    this.isAlreadyOnPlan = false;
    this.name = Lang.t('standardplan.complete');
    this.description = Lang.t('standardplan.complex-family-dynamics');
    this.subTitle = Lang.t('standardplan.estate-plan');
    this.upsellTitle = Lang.t('completeplan.everything-in-basic');
    this.upsellDescription = Lang.t('completeplan.create-an-optimized-estate');
    this.includedUpsells = SelfServeBillingPlanForm.getIncludedCompletePlanUpsells(isROC);
    this.excludedUpsells = this.getExcludedCompletePlanUpsells;
    this.productKey = ProductKey.INDIVIDUAL_COMPLETE_PLAN;
    this.recurringProductKey = ProductKey.INDIVIDUAL_COMPLETE_PLAN_RECURRING;
    this.price = isClient ? discountPrice : StandardPlanPrices.COMPLETE;
    this.yearlyPrice = StandardPlanPrices.COMPLETE_YEARLY;
    this.needs = this.getCompletePlanNeeds;
    this.isClient = isClient;
    this.isRecommended = isRecommended;
    this.type = VaultPlanType.COMPLETE.key;
    this.chargeRecurringFee = true;

    return this;
  }

  setAsProfessionalPlan(
    isClient: boolean = false,
    isRecommended: boolean = false,
    discountPrice: number | undefined = undefined,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    isROC: boolean | undefined = true
  ): this {
    this.isAlreadyOnPlan = false;
    this.name = Lang.t('standardplan.professional');
    this.description = Lang.t('standardplan.complete-estate-plan');
    this.subTitle = Lang.t('standardplan.estate-plan');
    this.upsellTitle = Lang.t('professionalplan.everything-in-complete');
    this.upsellDescription = Lang.t('professionalplan.get-support-from-multi');
    this.productKey = ProductKey.INDIVIDUAL_PROFESSIONAL_EXECUTOR_PLAN;
    this.recurringProductKey = ProductKey.INDIVIDUAL_PROFESSIONAL_EXECUTOR_PLAN_RECURRING;
    this.needs = this.getProfessionalPlanNeeds;
    this.includedUpsells = this.getIncludedProfessionalPlanUpsells;
    this.excludedUpsells = this.getExcludedProfessionalPlanUpsells;
    this.price = isClient ? discountPrice : StandardPlanPrices.PROFESSIONAL;
    this.yearlyPrice = StandardPlanPrices.PROFESSIONAL_YEARLY;
    this.isClient = isClient;
    this.isRecommended = isRecommended;
    this.type = VaultPlanType.PROFESSIONAL.key;
    this.chargeRecurringFee = true;

    return this;
  }

  setAsCustomPlan(): this {
    this.name = Lang.t('standardplan.individual-signature');
    this.productKey = ProductKey.INDIVIDUAL_CUSTOM_PLAN;
    this.price = undefined;
    this.yearlyPrice = undefined;
    this.type = VaultPlanType.CUSTOM.key;

    return this;
  }

  setAsCouplesCustomPlan(): this {
    this.name = Lang.t('standardplan.couples-signature');
    this.productKey = ProductKey.COUPLES_CUSTOM_PLAN;
    this.price = undefined;
    this.yearlyPrice = undefined;
    this.type = VaultPlanType.CUSTOM.key;

    return this;
  }

  setIsAlreadyOnPlan(value: boolean): this {
    this.isAlreadyOnPlan = value;
    return this;
  }

  setChargeRecurringFee(value: boolean): this {
    this.chargeRecurringFee = value;

    return this;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  isAvailableForJurisdiction(jurisdictionId: number): boolean {
    return true;
  }

  get actionText(): string {
    switch (this.type) {
      case VaultPlanType.STANDARD.key:
        return Lang.t('standardplan.start-now-pay-later');
      case VaultPlanType.BASIC.key:
        return Lang.t('selfserve.get-this-plan');
      default:
        return Lang.t('selfserve.book-free-consultation');
    }
  }

  get upsellActionText(): string {
    if (this.isAlreadyOnPlan) {
      return Lang.t('selfserve.continue-with-current-plan');
    }
    return Lang.t('selfserve.get-this-plan');
  }

  get formattedFinalPrice(): string {
    return NumeralService.formatValue(this.price, ColumnFormat.FORMAT_CURRENCY, 0);
  }

  get formattedYearlyPrice(): string {
    return NumeralService.formatValue(this.yearlyPrice, ColumnFormat.FORMAT_CURRENCY, 0);
  }

  get productKeys(): ProductKey[] {
    return [this.productKey, this.recurringProductKey].filter(
      (item?: ProductKey): item is ProductKey => !!item
    );
  }

  static getStandardPlanInclusions(isROC: boolean = true): string[] {
    return [
      isROC
        ? Lang.t('standardplan.appointment-of-executors')
        : Lang.t('standardplan.appointment-of-liquidators'),
      Lang.t('standardplan.distribution-to-your-spouse'),
      Lang.t('standardplan.specific-donations-and-gifts'),
      isROC
        ? Lang.t('standardplan.appointment-of-guardians')
        : Lang.t('standardplan.appointment-of-tutors'),
      Lang.t('basicplan.testamentary-trust'),
      Lang.t('standardplan.last-disposition-wishes'),
      isROC ? Lang.t('basicplan.poap-and-poac') : null,
      isROC ? Lang.t('basicplan.unlimited-edits-will') : null,
      Lang.t('standardplan.access-to-digital-vault'),
      Lang.t('standardplan.access-to-accounting-module')
    ].filter(Boolean);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  static getBasicPlanInclusions(isROC: boolean = true): string[] {
    return [
      Lang.t('standardplan.appointment-of-executors'),
      Lang.t('standardplan.distribution-to-your-spouse'),
      Lang.t('standardplan.specific-donations-and-gifts'),
      Lang.t('standardplan.appointment-of-guardians'),
      Lang.t('basicplan.testamentary-trust'),
      Lang.t('standardplan.last-disposition-wishes'),
      Lang.t('basicplan.poap-and-poac'),
      Lang.t('basicplan.viewing-distribution-tree'),
      Lang.t('basicplan.support-meeting-with'),
      Lang.t('basicplan.chat-support'),
      Lang.t('basicplan.unlimited-edits-will'),
      Lang.t('basicplan.unlimited-digital-vault'),
      Lang.t('basicplan.unlimited-accounting-module')
    ];
  }

  static getStandardPlanExclusions(isROC: boolean = true): string[] {
    return [
      !isROC ? Lang.t('billing.notarial-will-protection-mandate') : null,
      isROC ? Lang.t('standardplan.execution-documents-commissioner') : null,
      !isROC ? Lang.t('standardplan.hard-copy-of-your-will') : null,
      isROC
        ? Lang.t('standardplan.option-professional-executor')
        : Lang.t('standardplan.option-professional-liquidator'),
      Lang.t('standardplan.additional-clauses-to-setup-trusts'),
      Lang.t('standardplan.complex-distribution-scenarios'),
      Lang.t('standardplan.viewing-distribution-tree'),
      !isROC ? Lang.t('standardplan.protection-mandate-document') : null,
      Lang.t('standardplan.planning-for-a-business'),
      Lang.t('standardplan.expert-support-throughout'),
      Lang.t('standardplan.tax-optimization-strategies')
    ].filter(Boolean);
  }

  static getBasicPlanExclusions(isROC: boolean = true): string[] {
    return [
      !isROC ? Lang.t('billing.notarial-will-protection-mandate') : null,
      Lang.t('standardplan.execution-documents-commissioner'),
      Lang.t('basicplan.additional-clauses'),
      Lang.t('standardplan.option-professional-executor'),
      Lang.t('standardplan.complex-distribution-scenarios'),
      Lang.t('standardplan.planning-for-a-business'),
      Lang.t('standardplan.expert-guidance-throughout'),
      Lang.t('basicplan.comprehensive-estate-planning'),
      Lang.t('standardplan.tax-optimization-strategies')
    ].filter(Boolean);
  }

  get getStandardPlanNeeds(): string[] {
    return [
      Lang.t('lightdiagnostic.have-simple-estate'),
      Lang.t('lightdiagnostic.single-or-legally-married'),
      Lang.t('lightdiagnostic.simple-assets'),
      Lang.t('lightdiagnostic.dont-want-professional-support')
    ];
  }

  get getBasicPlanNeeds(): string[] {
    return [
      Lang.t('lightdiagnostic.help-from-professional'),
      Lang.t('lightdiagnostic.are-comfortable-with-technology'),
      Lang.t('lightdiagnostic.simple-assets-one-jurisdiction'),
      Lang.t('lightdiagnostic.simple-family-dynamics')
    ];
  }

  get getIncludedBasicPlanUpsells(): string[] {
    return [
      Lang.t('billingplan.create-your-will'),
      Lang.t('billingplan.create-your-poa'),
      Lang.t('billingplan.online-portal-access'),
      Lang.t('billingplan.chat-assistance-customer-support')
    ];
  }

  get getExcludedBasicPlanUpsells(): string[] {
    return [Lang.t('billingplan.no-add-on-service')];
  }

  get getCompletePlanNeeds(): string[] {
    return [
      Lang.t('lightdiagnostic.support-planning-process'),
      Lang.t('lightdiagnostic.single-married-divorced'),
      Lang.t('lightdiagnostic.children-with-disabilities'),
      Lang.t('lightdiagnostic.have-grandchildren'),
      Lang.t('lightdiagnostic.have-complex-family-dynamics-belnder-estranged'),
      Lang.t('lightdiagnostic.complex-financial-needs'),
      Lang.t('lightdiagnostic.want-to-set-up-structure')
    ];
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  static getIncludedCompletePlanUpsells(isROC: boolean = true): string[] {
    return [
      Lang.t('completeplan.professional-guidance'),
      Lang.t('completeplan.meeting-to-review-your-goals'),
      Lang.t('completeplan.chat-support'),
      Lang.t('completeplan.estate-plan-repport'),
      Lang.t('billingplan.add-on-services')
    ];
  }

  get getExcludedCompletePlanUpsells(): string[] {
    return [];
  }

  get getProfessionalPlanNeeds(): string[] {
    return [
      Lang.t('lightdiagnostic.dont-have-responsible-candidates'),
      Lang.t('lightdiagnostic.team-of-professionals-after-passing'),
      Lang.t('lightdiagnostic.team-of-professionals-incapacitated')
    ];
  }

  get getIncludedProfessionalPlanUpsells(): string[] {
    return [
      Lang.t('professionalplan.professional-support-from'),
      Lang.t('professionalplan.storage-of-original-will'),
      Lang.t('professionalplan.professional-executor-appointed'),
      Lang.t('professionalplan.professional-poa'),
      Lang.t('billingplan.add-on-services')
    ];
  }

  get getExcludedProfessionalPlanUpsells(): string[] {
    return [];
  }

  get isCustomPlan(): boolean {
    return this.type === VaultPlanType.CUSTOM.key;
  }

  get isStandardPlan(): boolean {
    return this.type === VaultPlanType.STANDARD.key;
  }

  get isBasicPlan(): boolean {
    return this.type === VaultPlanType.BASIC.key;
  }

  get isCompletePlan(): boolean {
    return this.type === VaultPlanType.COMPLETE.key;
  }

  get isProfessionalPlan(): boolean {
    return this.type === VaultPlanType.PROFESSIONAL.key;
  }

  get isCouplesPlan(): boolean {
    return BillingPlanIdMapper.isCouplesPlan(this.billingPlanId!);
  }

  get legalDisclaimer(): string | null {
    if (this.isStandardPlan) {
      return Lang.t('billing.clearestate-standard-legal');
    }

    if (this.isBasicPlan) {
      return Lang.t('billing.clearestate-basic-legal');
    }

    return null;
  }
}
